import { useState } from "react";
import { IndianRupee, Wallet, TrendingUp, ShieldCheck, Layers, CreditCard, Percent, Radio, AlertTriangle, Download } from "lucide-react";
import { inr } from "@/lib/api";
import { Skeleton } from "@/components/common";
import { useAnalytics, Toolbar, Kpi, Panel, TrendArea, Bars, Donut, RankTable, Gauge, Funnel, inrShort, CHART_COLORS } from "@/lib/adminUI";
import { exportCsv } from "@/lib/exportCsv";

const TABS = ["Executive", "DPD & Bucket", "Payment Types", "Wallet & Rewards", "Discount & Margin", "Lender Sync"];
const CONF_STATES = [["ALL", "All confirmation"], ["AWAITING_LENDER_CONFIRMATION", "Awaiting"], ["LENDER_CONFIRMED", "Confirmed"], ["LENDER_REJECTED", "Rejected"]];
const selCls = "rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#0B132B] outline-none focus:border-[#14B8A6]";

function NotFromLender({ msg = "Not available from lender" }) {
  return <div className="flex items-center gap-2 rounded-lg bg-slate-50 p-4 text-sm italic text-slate-400" data-testid="not-from-lender"><AlertTriangle size={15} /> {msg}</div>;
}

export default function CollectionIntelligence() {
  const a = useAnalytics();
  const [tab, setTab] = useState("Executive");
  if (!a.data) return <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-72" /></div>;
  const d = a.data;
  const ci = d.collection_intel, w = d.wallet, m = d.margin, dpd = d.dpd_buckets, f = d.funnel, pts = d.payment_types;
  const lenderNames = (d.lenders || []).map((l) => l.lender);

  const FilterBar = () => (
    <div className="flex flex-wrap items-center gap-2" data-testid="collection-filters">
      <select value={a.filters.lender} onChange={(e) => a.setFilters({ ...a.filters, lender: e.target.value })} className={selCls} data-testid="ci-lender">
        <option value="ALL">All lenders</option>{lenderNames.map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
      <select value={a.filters.payment_type} onChange={(e) => a.setFilters({ ...a.filters, payment_type: e.target.value })} className={selCls} data-testid="ci-ptype">
        {["ALL", "EMI", "OVERDUE", "POS", "CUSTOM"].map((t) => <option key={t} value={t}>{t === "ALL" ? "All types" : t}</option>)}
      </select>
      <select value={a.filters.confirmation_status} onChange={(e) => a.setFilters({ ...a.filters, confirmation_status: e.target.value })} className={selCls} data-testid="ci-conf">
        {CONF_STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <div className="admin-console space-y-5" data-testid="collection-intel-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B132B]">Collection Command Center</h1>
          <p className="text-sm text-slate-500">Collection health, lender portfolio, payment performance & unit economics — on real data.</p>
        </div>
        <Toolbar a={a} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {TABS.map((t) => <button key={t} data-testid={`ci-tab-${t}`} onClick={() => setTab(t)} className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === t ? "bg-[#0B132B] text-white" : "text-slate-500 hover:text-[#0B132B]"}`}>{t}</button>)}
        </div>
        <FilterBar />
      </div>

      {tab === "Executive" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label="Portfolio POS" value={inrShort(ci.portfolio_pos)} icon={Layers} accent="#0EA5E9" hint="Lender-provided" />
            <Kpi label="Overdue (Lender)" value={inrShort(ci.lender_overdue)} icon={AlertTriangle} accent="#EF4444" />
            <Kpi label="EMI Due" value={inrShort(ci.emi_due)} icon={CreditCard} accent="#F59E0B" />
            <Kpi label="Collected" value={inrShort(ci.collected)} icon={IndianRupee} accent="#10B981" />
            <Kpi label="Collection Pending" value={inrShort(ci.collection_pending)} icon={Wallet} accent="#6366F1" />
            <Kpi label="Pending Confirmation" value={ci.pending_confirmation_count} icon={ShieldCheck} accent="#F59E0B" hint={inrShort(ci.pending_confirmation_amount)} />
            <Kpi label="Failed Payments" value={ci.failed_count} icon={AlertTriangle} accent="#EF4444" hint={inrShort(ci.failed_amount)} />
            <Kpi label="Wallet Used" value={inrShort(ci.wallet_used)} icon={Wallet} accent="#8B5CF6" />
            <Kpi label="Rewards Credited" value={inrShort(ci.rewards_credited)} icon={TrendingUp} accent="#10B981" />
            <Kpi label="Net Margin" value={inrShort(ci.net_margin)} icon={Percent} accent="#0B132B" hint="Admin only" />
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Lender Confirmed Collection" value={inrShort(ci.lender_confirmed_collected)} icon={ShieldCheck} accent="#10B981" hint="Only lender-confirmed" />
            <Kpi label="Awaiting Confirmation" value={inrShort(ci.awaiting_amount)} icon={IndianRupee} accent="#F59E0B" hint={`${ci.pending_confirmation_count} txns`} />
            <Kpi label="Lender Rejected" value={ci.rejected_count} icon={AlertTriangle} accent="#EF4444" />
            <Kpi label="Confirmation Rate" value={`${ci.confirmation_rate}%`} icon={TrendingUp} accent="#6366F1" />
          </div>
          <div className="grid gap-4 lg:grid-cols-4">
            <Panel title="Collection Success"><Gauge value={ci.success_rate} label="Success" /></Panel>
            <Panel title="Lender Confirmation"><Gauge value={ci.confirmation_rate} label="Confirmed" color="#6366F1" /></Panel>
            <Panel title="Payment Lifecycle Funnel" className="lg:col-span-2"><Funnel data={f} /></Panel>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Collection Trend" className="lg:col-span-2"><TrendArea data={d.collections.series} /></Panel>
            <Panel title="Payment Status"><Donut data={d.payments.status_counts} /></Panel>
          </div>
        </div>
      )}

      {tab === "DPD & Bucket" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-teal-50 p-3 text-xs text-[#0B132B]" data-testid="lender-source-note">
            <b>Source: Lender / API.</b> DPD, bucket, NORM/STAB/NPA, settlement & closure are lender-controlled. ZRo Pe never calculates these independently.
          </div>
          {!dpd.has_lender_data ? <NotFromLender /> : (
            <>
              <div className="grid gap-4 lg:grid-cols-2">
                <Panel title="Bucket Distribution" subtitle="Accounts by lender bucket"><Donut data={dpd.buckets.map((b) => ({ name: b.bucket, value: b.accounts }))} /></Panel>
                <Panel title="Bucket × POS / Overdue / Collection"><Bars data={dpd.buckets.map((b) => ({ date: b.bucket, POS: b.pos, Overdue: b.overdue, Collection: b.collection }))} keys={["POS", "Overdue", "Collection"]} money /></Panel>
              </div>
              <Panel title="Bucket Detail" right={<span className="text-xs italic text-slate-400">Historical movement unavailable</span>}>
                <RankTable cols={["Bucket", "Accounts", "POS", "Overdue", "Collection", "Collection %"]} rows={dpd.buckets}
                  render={(b) => (
                    <tr key={b.bucket} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-[#0B132B]">{b.bucket}</td>
                      <td className="px-4 py-3">{b.accounts}</td>
                      <td className="px-4 py-3 text-slate-600">{b.bucket === "Not available from lender" ? "—" : inr(b.pos)}</td>
                      <td className="px-4 py-3 text-slate-600">{b.bucket === "Not available from lender" ? "—" : inr(b.overdue)}</td>
                      <td className="px-4 py-3 font-semibold text-emerald-600">{inr(b.collection)}</td>
                      <td className="px-4 py-3">{b.bucket === "Not available from lender" ? "—" : `${b.collection_rate}%`}</td>
                    </tr>
                  )} />
              </Panel>
              {dpd.not_available_count > 0 && <NotFromLender msg={`${dpd.not_available_count} active account(s) have no lender-provided bucket — shown as "Not available from lender".`} />}
            </>
          )}
        </div>
      )}

      {tab === "Payment Types" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button data-testid="ci-export-ptypes" onClick={() => exportCsv(pts.map((p) => ({ Type: p.type, Transactions: p.count, "Success Amount": p.success_amount, "Failed Amount": p.failed_amount, "Pending Confirmation": p.pending_confirmation_amount, Avg: p.avg, "Collection %": p.collection_rate, "Wallet Used": p.wallet_used, "Customer Cash": p.customer_cash, Discount: p.discount, "Reward Cost": p.reward_cost })), "ZRoPe-PaymentTypes")} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> CSV</button>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Collection by Payment Type"><Bars data={pts.map((p) => ({ date: p.type, amount: p.success_amount }))} keys={["amount"]} money /></Panel>
            <Panel title="Type Share (transactions)"><Donut data={pts.filter((p) => p.count > 0).map((p) => ({ name: p.type, value: p.count }))} /></Panel>
          </div>
          <Panel title="Payment Type Intelligence">
            <RankTable cols={["Type", "Txns", "Success ₹", "Failed ₹", "Pending Conf.", "Avg", "Coll. %", "Wallet", "Cash", "Discount", "Reward Cost"]} rows={pts}
              render={(p) => (
                <tr key={p.type} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-[#0B132B]">{p.type}</td>
                  <td className="px-4 py-3">{p.count}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-600">{inr(p.success_amount)}</td>
                  <td className="px-4 py-3 text-red-500">{inr(p.failed_amount)}</td>
                  <td className="px-4 py-3 text-amber-600">{inr(p.pending_confirmation_amount)}</td>
                  <td className="px-4 py-3 text-slate-600">{inr(p.avg)}</td>
                  <td className="px-4 py-3">{p.collection_rate}%</td>
                  <td className="px-4 py-3 text-purple-600">{inr(p.wallet_used)}</td>
                  <td className="px-4 py-3 text-slate-600">{inr(p.customer_cash)}</td>
                  <td className="px-4 py-3 text-slate-600">{inr(p.discount)}</td>
                  <td className="px-4 py-3 text-slate-600">{inr(p.reward_cost)}</td>
                </tr>
              )} />
          </Panel>
          <p className="text-xs text-slate-400">A custom/partial payment only facilitates a customer-chosen or lender-approved amount. It does not by itself reduce DPD, change bucket, settle or close the loan — those outcomes are confirmed by the lender.</p>
        </div>
      )}

      {tab === "Wallet & Rewards" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Available Balance" value={inrShort(w.available_balance)} icon={Wallet} accent="#10B981" />
            <Kpi label="Pending Rewards" value={inrShort(w.pending_balance)} icon={ShieldCheck} accent="#F59E0B" />
            <Kpi label="Total Earned" value={inrShort(w.total_earned)} icon={TrendingUp} />
            <Kpi label="Total Used" value={inrShort(w.total_used)} icon={IndianRupee} accent="#8B5CF6" />
            <Kpi label="Wallet Liability" value={inrShort(w.liability)} icon={Layers} accent="#EF4444" hint="Available + Pending" />
            <Kpi label="Reward Cost" value={inrShort(w.reward_cost)} icon={Percent} accent="#0B132B" hint="Credited only" />
            <Kpi label="Customers w/ Balance" value={w.customers_with_balance} icon={ShieldCheck} accent="#0EA5E9" />
            <Kpi label="Customers Using Wallet" value={w.customers_using_wallet} icon={Wallet} accent="#6366F1" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Reward States" subtitle="Gateway success ≠ lender confirmation ≠ reward credit">
              <Donut data={[{ name: "Pending", value: w.rewards_pending }, { name: "Credited", value: w.rewards_credited }, { name: "Used", value: w.rewards_used }, { name: "Reversed", value: w.rewards_reversed }].filter((x) => x.value > 0)} money />
            </Panel>
            <Panel title="Reward Lifecycle (from funnel)"><Funnel data={f.slice(2)} /></Panel>
          </div>
        </div>
      )}

      {tab === "Discount & Margin" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-[#0B132B] p-3 text-xs font-semibold text-[#14B8A6]" data-testid="margin-admin-note">ADMIN ONLY — margin, reward cost & gross values are never shown to customers.</div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label="Gross" value={inrShort(m.gross)} icon={IndianRupee} />
            <Kpi label="Discount" value={inrShort(m.discount)} icon={Percent} accent="#F59E0B" hint={`${m.discount_pct}%`} />
            <Kpi label="Reward Cost" value={inrShort(m.reward_cost)} icon={Wallet} accent="#8B5CF6" hint={`${m.reward_pct}%`} />
            <Kpi label="Net Margin" value={inrShort(m.net_margin)} icon={TrendingUp} accent="#10B981" hint={`${m.margin_pct}%`} />
            <Kpi label="Margin %" value={`${m.margin_pct}%`} icon={Percent} accent="#0B132B" />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="margin-formula">
            <b className="text-[#0B132B]">Gross ({inr(m.gross)})</b> − Discount ({inr(m.discount)}) − Reward Cost ({inr(m.reward_cost)}) = <b className="text-emerald-600">Net Margin {inr(m.net_margin)}</b>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Margin by Payment Type"><Bars data={m.by_type.map((x) => ({ date: x.name, Gross: x.gross, Discount: x.discount, "Reward Cost": x.reward_cost, "Net Margin": x.net_margin }))} keys={["Gross", "Net Margin"]} colors={[CHART_COLORS[1], CHART_COLORS[0]]} money /></Panel>
            <Panel title="Margin by Lender">
              <RankTable cols={["Lender", "Gross", "Discount", "Reward Cost", "Net Margin", "Margin %"]} rows={m.by_lender}
                render={(x) => (
                  <tr key={x.name} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-[#0B132B]">{x.name}</td>
                    <td className="px-4 py-3 text-slate-600">{inr(x.gross)}</td>
                    <td className="px-4 py-3 text-amber-600">{inr(x.discount)}</td>
                    <td className="px-4 py-3 text-purple-600">{inr(x.reward_cost)}</td>
                    <td className="px-4 py-3 font-semibold text-emerald-600">{inr(x.net_margin)}</td>
                    <td className="px-4 py-3">{x.margin_pct}%</td>
                  </tr>
                )} />
            </Panel>
          </div>
        </div>
      )}

      {tab === "Lender Sync" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-700" data-testid="sync-demo-note">DEMO / SAMPLE DATA — no real lender API is connected yet. Architecture is API-ready for future Bank/NBFC integration.</div>
          <div className="flex justify-end">
            <button data-testid="ci-export-sync" onClick={() => exportCsv(d.sync_health.map((s) => ({ Lender: s.lender, Status: s.status, Source: s.source, "Records Synced": s.records_synced, "Records Failed": s.records_failed, "Last Synced": s.last_synced_at || "", "Last Error": s.last_error || "" })), "ZRoPe-LenderSync")} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> CSV</button>
          </div>
          <Panel title="Lender API Sync Health" subtitle="Connected lenders & sync status">
            <RankTable cols={["Lender", "Auto Status", "Source", "Awaiting", "Synced", "Failed", "Last Sync"]} rows={d.sync_health}
              render={(s) => {
                const auto = s.auto_status || "Auto Sync Active";
                const dot = auto === "Auto Sync Active" ? "bg-emerald-500" : auto === "Awaiting Confirmation" ? "bg-amber-400" : auto === "Sync Delayed" ? "bg-orange-500" : "bg-red-500";
                return (
                  <tr key={s.lender} className="hover:bg-slate-50" data-testid={`sync-row-${s.lender}`}>
                    <td className="px-4 py-3 font-medium text-[#0B132B]"><Radio size={13} className="mr-1 inline text-[#14B8A6]" />{s.lender}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B132B]"><span className={`h-2 w-2 rounded-full ${dot}`} />{auto}{s.is_demo ? " (DEMO)" : ""}</span></td>
                    <td className="px-4 py-3">{s.is_demo ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">DEMO</span> : s.source}</td>
                    <td className="px-4 py-3 text-amber-600">{s.awaiting_count || 0}</td>
                    <td className="px-4 py-3 text-emerald-600">{s.records_synced}</td>
                    <td className="px-4 py-3 text-red-500">{s.records_failed}</td>
                    <td className="px-4 py-3 text-slate-500">{s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : "Not available from lender"}</td>
                  </tr>
                );
              }} />
          </Panel>
        </div>
      )}
    </div>
  );
}
